/* eslint-disable @typescript-eslint/camelcase */
import { Avatar, Grid, makeStyles, Typography } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import EditIcon from '@material-ui/icons/Edit';
import Button from 'components/Button';
import { Formik } from 'formik';
import i18n from 'i18n';
import { getAllCompaniesRequest } from 'modules/companies/actions';
import { CompanyFields } from 'modules/companies/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import moment from 'moment';
import { GetOperatorFields } from 'modules/operators/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import { useFilePicker } from 'use-file-picker';
import { regexExp } from 'utils/helpers';
import { StatusIcon } from 'components/TableItems';
import {
  resetMFAToOperatorRequest,
  toggleBlockOperatorRequest,
} from 'modules/operators/actions';
import clsx from 'clsx';
import Loading from 'components/Loading';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import AutoCard from 'components/AutoCard';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import screenFields from './formFields';
import { initialEdit } from './initialValues';
import validationSchema from './schema';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 500,
  search: '',
};

interface FormProps {
  isLoading: boolean;
  companyLoading: boolean;
  isEdit?: boolean;
  initialValues?: GetOperatorFields | null;
  companyList: CompanyFields[];
  companySelected: CompanyData | null;
  role?: RolesEnum;
  onSubmit: (values: any, form: any) => void;
  getAllCompaniesRequest: (props) => Promise<void>;
}

const ProfileCard = ({
  isLoading,
  companyLoading,
  isEdit,
  role,
  companySelected,
  initialValues,
  companyList,
  onSubmit,
  getAllCompaniesRequest,
}: FormProps) => {
  const classes = useStyles();
  const [formValues, setFormValues] = React.useState<GetOperatorFields>(
    initialEdit
  );

  const [openFileSelector, { filesContent }] = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false,
    maxFileSize: 10,
  });

  React.useEffect(() => {
    getAllCompaniesRequest(initialQuery);
  }, [getAllCompaniesRequest]);

  const formatPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return '';

    return !regexExp.numbersOnly.test(phoneNumber)
      ? `55${phoneNumber.replace(/\D/g, '')}`
      : phoneNumber;
  };

  const setLanguage = (values: GetOperatorFields) => {
    if (values?.language === 'en') {
      return i18n.t('LANGUAGES.ENGLISH_KEY');
    }
    if (values?.language === 'es') {
      return i18n.t('LANGUAGES.SPANISH_KEY');
    }
    return i18n.t('LANGUAGES.PORTUGUESE_KEY');
  };

  React.useEffect((): void => {
    screenFields.forEach(item => {
      let options = item?.options;
      let disabled = item?.disabled;
      if (item?.stateKey === 'company.company_uuid') {
        options =
          companyList.map(item => ({
            value: item?.company_uuid,
            label: item?.company_name,
          })) || [];
      }

      if (item?.stateKey === 'email') {
        disabled = isEdit;
      }

      return {
        ...item,
        options,
        disabled,
      };
    });
  }, [companyList, companyLoading, isEdit]);

  const changeInitialValues = React.useCallback(() => {
    if (!initialValues) return initialEdit;
    return {
      ...initialValues,
      company_uuid: initialValues?.company?.company_uuid,
    };
  }, [initialValues]);

  React.useEffect(() => {
    setFormValues(changeInitialValues());
  }, [changeInitialValues]);

  const dateFormat = (date: string) =>
    moment(new Date(date))
      .local()
      .format(
        i18n.language === 'en-US'
          ? `MM/DD/YYYY [${i18n.t('OPERATORS_PAGE.TIME_AT_KEY')}] hh:mm`
          : `DD/MM/YYYY [${i18n.t('OPERATORS_PAGE.TIME_AT_KEY')}] HH:mm`
      );

  return (
    <>
      <Grid item xs={12} className={classes.topContent}>
        <Button
          id="open-file-selector"
          className={classes.buttonAvatar}
          onClick={openFileSelector}
          disabled
        >
          <Avatar
            variant="rounded"
            src={filesContent[0]?.content}
            className={classes.avatar}
          />
          <EditIcon className={classes.hiddenIcon} />
        </Button>
      </Grid>
      <AutoCard>
        <Grid item xs={12} className={classes.formContainer}>
          <Formik
            initialValues={{
              ...formValues,
              phone: formatPhoneNumber(formValues.phone),
              mobile_phone: formatPhoneNumber(formValues.mobile_phone),
            }}
            validationSchema={validationSchema[isEdit ? 'edit' : 'create']}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({ values }) => (
              <>
                <Grid container xs={12} className={classes.background}>
                  {isLoading || values.first_name === undefined ? (
                    <Grid container className={classes.container}>
                      <Loading />
                    </Grid>
                  ) : (
                    <>
                      <Grid container className={classes.container}>
                        <PersonIcon
                          className={clsx(classes.personIconDefault, {
                            [classes.personIconSuperAdmin]: inSuperAdminView(
                              role,
                              companySelected
                            ),
                            [classes.personIconAdmin]: inAdminView(
                              role,
                              companySelected
                            ),
                            [classes.personIconOperator]: inOperatorView(
                              role,
                              companySelected
                            ),
                          })}
                        ></PersonIcon>
                        <Typography className={classes.cardName}>
                          {`${values.first_name} ${values.last_name}`}
                        </Typography>
                      </Grid>
                      <Grid className={classes.responsiveGrid}>
                        <Grid container xs={6}>
                          <Grid container xs={12} className={classes.cardInfos}>
                            <Typography className={classes.cardTitles}>
                              {i18n.t('EMAIL_KEY')}
                              {': '}
                            </Typography>
                            <Typography className={classes.cardValues}>
                              {values?.email}
                            </Typography>
                          </Grid>
                          <Grid container xs={12} className={classes.cardInfos}>
                            <Typography className={classes.cardTitles}>
                              {'Role:'}{' '}
                            </Typography>
                            <Typography className={classes.cardValues}>
                              {i18n.t(
                                `ROLES.${values?.role?.toUpperCase()}_KEY`
                              )}
                            </Typography>
                          </Grid>
                          <Grid container xs={12} className={classes.cardInfos}>
                            <Typography className={classes.cardTitles}>
                              {i18n.t('LANGUAGE_KEY')}
                              {': '}
                            </Typography>
                            <Typography className={classes.cardValues}>
                              {setLanguage(values)}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid container xs={6} className={classes.cardContent}>
                          <Grid container xs={12} className={classes.cardInfos}>
                            <Typography className={classes.cardTitles}>
                              {i18n.t('COMPANY_KEY')}
                              {': '}
                            </Typography>
                            <Typography className={classes.cardValues}>
                              {values?.company?.company_name}
                            </Typography>
                          </Grid>
                          <Grid container xs={12} className={classes.cardInfos}>
                            <Typography className={classes.cardTitles}>
                              {i18n.t('STATUS_KEY')}
                              {': '}
                            </Typography>
                            <Typography>
                              {values?.blocked === true ? (
                                <Grid container className={classes.alignLines}>
                                  <Typography className={classes.spacingText}>
                                    {i18n.t('STATUS_OPERATOR_BLOCKED_KEY')}
                                  </Typography>
                                  <StatusIcon status="blocked" />
                                </Grid>
                              ) : (
                                <Grid container className={classes.alignLines}>
                                  <Typography className={classes.spacingText}>
                                    {i18n.t('STATUS_OPERATOR_ACTIVE_KEY')}
                                  </Typography>
                                  <StatusIcon status="success" />
                                </Grid>
                              )}
                            </Typography>
                          </Grid>
                          {values?.mfa_required && (
                            <Grid
                              container
                              xs={12}
                              className={classes.cardInfos}
                            >
                              <Typography className={classes.cardTitles}>
                                {i18n.t('STATUS_MFA_CONFIGURED_KEY')}
                                {': '}
                              </Typography>
                              <Typography>
                                {!values?.mfa_configured ? (
                                  <Grid
                                    container
                                    className={classes.alignLines}
                                  >
                                    <Typography className={classes.spacingText}>
                                      {i18n.t('INACTIVE_KEY')}
                                    </Typography>
                                    <StatusIcon status="blocked" />
                                  </Grid>
                                ) : (
                                  <Grid
                                    container
                                    className={classes.alignLines}
                                  >
                                    <Typography className={classes.spacingText}>
                                      {i18n.t('STATUS_OPERATOR_ACTIVE_KEY')}
                                    </Typography>
                                    <StatusIcon status="success" />
                                  </Grid>
                                )}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      <Grid container xs={5} className={classes.creationInfo}>
                        <Grid container>
                          {' '}
                          {values?.created_by_operator && (
                            <Typography className={classes.creationText}>
                              {`${i18n.t('OPERATORS_PAGE.CREATED_BY_KEY')}: ${
                                values?.created_by_operator?.first_name
                              } ${values?.created_by_operator?.last_name} (${
                                values?.created_by_operator?.email
                              })`}
                            </Typography>
                          )}
                        </Grid>
                        <Grid container>
                          <Typography className={classes.creationText}>
                            {`${i18n.t(
                              'OPERATORS_PAGE.CREATED_AT_KEY'
                            )}: ${dateFormat(values?.created_date)}`}
                          </Typography>
                          <Typography
                            variant="body1"
                            className={classes.creationText}
                          ></Typography>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Grid>
              </>
            )}
          </Formik>
        </Grid>
      </AutoCard>
    </>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data },
  companies: { companyList, isLoading },
  errors: { fieldsErrors },
}: ApplicationState) => ({
  fieldsErrors,
  companyList: companyList.results,
  companySelected,
  companyLoading: isLoading,
  role: data?.role,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  getAllCompaniesRequest,
  resetMFAToOperatorRequest,
  toggleBlockOperatorRequest,
})(ProfileCard);
